import React, { useState } from 'react';
import { useForm, SubmitHandler } from 'react-hook-form';
import { forgotPassword, login } from '../services/auth.services';
import { useAuthentication } from '../services/auth.provider';
import * as HttpStatus from 'http-status-codes';
import { useNavigate } from 'react-router';
import logo from '../../../assets/images/logo.svg';
import portrait from '../../../assets/images/portrait.jpg';
import { Button, Grid2 } from '@mui/material';
import { makeStyles } from '@mui/styles';
import theme from '../../../core/theme';
import ForgotPasswordModal from './forgotPassword';
import Toaster from '../../shared/components/Toaster';
import { isEmpty } from 'lodash';
import { CloseEyeIcon, OpenEyeIcon } from '../../shared/components/AppIcons';

type FormValues = {
  password: string;
  email: string;
};

const useStyles = makeStyles({
  container: {
    width: '1500px',
    height: '650px',
  },
  text: {
    fontSize: '16px',
    '& .highlightedText': {
      textDecoration: 'none',
      color: `${theme.palette.error.main} !important`,
    },
  },
  button: {
    width: '427px',
    height: '38px',
    backgroundColor: '#F67570 !important',
  },
});

const Login: React.FC = () => {
  const classes = useStyles();
  const {
    register,
    formState: { errors },
    handleSubmit,
    setValue,
  } = useForm<FormValues>({ mode: 'onBlur' });
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [show, setShow] = useState<boolean>(false);
  const [action, setAction] = useState<string>('');
  const [toasterMessage, setToasterMessage] = useState('');
  const [openForgotPasswordModal, setOpenForgotPasswordModal] = useState<boolean>(false);
  const [hidden, setHidden] = useState<Boolean>(true);
  const auth = useAuthentication();
  const navigate = useNavigate();

  const getToaster = (actionString: string) => (
    <Toaster messageType={actionString} show={show} setShow={setShow} message={`${toasterMessage}`} />
  );

  const onSubmit: SubmitHandler<FormValues> = async (form) => {
    setErrorMessage('');
    try {
      const user = await login(form.email, form.password);
      auth.login(user);
      window.location.href = '/dashboard';
    } catch (error) {
      navigate(`/login`);

      if (error?.response?.status === HttpStatus.UNAUTHORIZED) {
        setErrorMessage('Incorrect email/Username or Password');
      } else if (error?.response?.status === HttpStatus.NOT_FOUND) {
        setErrorMessage('Invalid user');
      } else {
        setErrorMessage('Please try again');
      }
    }
  };

  const handleConfirm = async (forgotEmailId: string) => {
    try {
      /*  trigger email notification to reset password */
      const response = await forgotPassword(forgotEmailId);
      console.log('response', response);
      setAction('success');
      setToasterMessage('Password reset link sent to your email,. Please check your inbox.');
      setShow(true);
      setOpenForgotPasswordModal(false);
    } catch (error) {
      setErrorMessage(error?.message || 'Please try again');
      setAction('failure');
      setToasterMessage('Unable to send reset link. Please try again later.');
      setShow(true);
      throw error;
    }
  };

  const handleSignUp = () => {
    navigate(`/signup`);
  };

  return (
    <>
      {!isEmpty(action) && getToaster(action)}

      <Grid2 container direction="row" className={classes.container} justifyContent="center">
        <Grid2 container mr={12} direction="column">
          <img src={logo} width="145px" height="145px" alt="appLogo" />
          <Grid2 container mb={6} direction="column">
            <Grid2 mb={2} mt={3}>
              <h1>Login to your Account</h1>
            </Grid2>
            <span className={classes.text}>Welcome back!</span>

            <form onSubmit={handleSubmit(onSubmit)}>
              <Grid2 container mb={2}>
                <div className="text-danger mb-1 pb-5 text-center">{errorMessage}</div>
              </Grid2>
              <Grid2 container mt={8} direction="column">
                <label htmlFor="inputEmail" className={classes.text}>
                  Email Address
                  <input
                    {...register('email', {
                      required: 'Email or Username is Required',
                      pattern: {
                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}|[A-Z0-9_-]$/i,
                        message: 'Invalid email address or Username',
                      },
                    })}
                    name="email"
                    id="inputEmail"
                    className={`form-control ${errors.email && 'is-invalid'}`}
                    placeholder="Enter your Email Address"
                    onChange={(e) => {
                      setValue('email', e.target.value);
                    }}
                  />
                  {errors.email && <span>{errors.email.message}</span>}
                </label>
              </Grid2>
              <Grid2 container mt={3} direction="column">
                <label htmlFor="inputPassword" className={classes.text}>
                  Password
                  <div style={{ position: 'relative', width: '100%' }}>
                    <input
                      {...register('password', { required: 'Password is required' })}
                      type={hidden ? 'password' : 'text'}
                      name="password"
                      id="inputPassword"
                      className={`form-control ${errors.password ? 'is-invalid' : ''}`}
                      placeholder="Password"
                      onChange={(e) => setValue('password', e.target.value)}
                      style={{ paddingRight: '35px', width: '100%' }} // space for icon
                    />

                    {/* Eye icon inside the input */}
                    <span
                      onClick={() => setHidden(!hidden)}
                      style={{
                        position: 'absolute',
                        right: '10px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        cursor: 'pointer',
                      }}
                    >
                      {hidden ? <CloseEyeIcon /> : <OpenEyeIcon />}
                    </span>
                  </div>
                  {errors.password && (
                    <span style={{ color: 'red', fontSize: '0.9em' }}>{errors.password.message}</span>
                  )}
                </label>
              </Grid2>
              <Grid2 container direction="row" justifyContent="space-between" mt={2}>
                <Grid2 container className={classes.text} justifyContent="flex-end">
                  <label className={classes.text}>
                    <input
                      type="checkbox"
                      name="rememberMe"
                      style={{ width: '20px', height: '20px', marginRight: '5px' }}
                    />
                    Remember Me
                  </label>
                </Grid2>
                <Grid2 container className={classes.text} justifyContent="flex-end">
                  <Button
                    size="medium"
                    variant="text"
                    type="button"
                    sx={{
                      color: '#F67570',
                      textTransform: 'none',
                      fontFamily: 'Lato',
                      padding: '0 8px',
                    }}
                    onClick={() => setOpenForgotPasswordModal(true)}
                  >
                    Forgot Password?
                  </Button>
                </Grid2>
              </Grid2>
              <Grid2 container justifyContent="center" mt={3}>
                <Button variant="contained" type="submit" className={classes.button}>
                  Sign in
                </Button>
              </Grid2>
              <Grid2 container direction="row" justifyContent="space-between" mt={2}>
                <Grid2>
                  <span className={classes.text}>Don’t have an account?</span>
                </Grid2>
                <Grid2 className={classes.text}>
                  <a href="/signup" className="highlightedText" onClick={handleSignUp}>
                    Create an account
                  </a>
                </Grid2>
              </Grid2>
            </form>
          </Grid2>
        </Grid2>
        <Grid2 container>
          <img src={portrait} width="480" height="638" alt="sideImage" />
        </Grid2>

        {openForgotPasswordModal && (
          <ForgotPasswordModal
            setShowAlert={setOpenForgotPasswordModal}
            confirm={handleConfirm}
            setErrorMessage={setErrorMessage}
          />
        )}
      </Grid2>
    </>
  );
};

export default Login;
